import pricingLogo from "../../../assets/images/merchandise/pricing-logo.svg";
import advertisingLogo from "../../../assets/images/merchandise/advertising-logo.svg";
import contentLogo from "../../../assets/images/merchandise/content-logo.svg";
import { Link } from "@tanstack/react-router";
import { useAppSelector } from "../../../redux/hooks";
import { WorkflowProgressV2Banner } from "../../../modules/workflow-progress-v2/components/WorkflowProgressV2Banner";
import type { ReactNode } from "react";
import { buttonRecipe } from "../../core/basic/Button";
import { PageContainer } from "../../core/basic/PageContainer";
import { css } from "../../../../styled-system/css";
import { Box, Center, Flex, VStack } from "../../../../styled-system/jsx";
import Card from "../../core/basic/Card";
import { useTranslation } from "react-i18next";

interface CTAProps {
  title: string;
  icon: string;
  children: ReactNode;
  to: string;
  toText: string;
}

interface EmptyStateProps {
  type: string;
}

const CTA = ({ title, icon, children, to, toText }: CTAProps) => {
  return (
    <Card css={{ flex: 1, minWidth: "300px", maxWidth: "400px" }}>
      <VStack gap="medium" width="100%" padding="medium" fontSize={"medium"}>
        <img
          src={icon}
          className={css({
            display: "block",
            width: "180px",
            height: "180px",
          })}
        />
        <h3 className={css({ margin: 0, fontWeight: "normal" })}>{title}</h3>
        <div className={css({ minHeight: "200px" })}>{children}</div>
        <Link to={to} className={buttonRecipe()}>
          {toText}
        </Link>
      </VStack>
    </Card>
  );
};

const EmptyState = ({ type }: EmptyStateProps) => {
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const { t } = useTranslation(["merchandising", "common"]);

  return (
    <PageContainer gap="medium" alignItems={"center"} paddingY="large" paddingX="0">
      <Box width="100%" className={css({ marginTop: "-24px", _empty: { margin: 0 } })}>
        <WorkflowProgressV2Banner />
      </Box>

      <h3 className={css({ margin: 0 })}>{t(($) => $.merchandising["Welcome to Trellis"])}</h3>

      {type === "NO_DATA" && (
        <>
          <p className={css({ margin: 0 })}>
            {t(($) => $.merchandising["Please connect to your marketplace to get started."])}
          </p>

          {channel === "walmart" ?
            <Link to="/user/walmartsummary" className={buttonRecipe()}>
              {t(($) => $.common.sidebar["Walmart Connect"])}
            </Link>
          : <Link to="/user/amzsummary" className={buttonRecipe()}>
              {t(($) => $.common.sidebar["Amazon Connect"])}
            </Link>
          }
        </>
      )}
      {type === "COLLECTING_DATA" && (
        <p className={css({ textAlign: "center" })}>
          {t(($) => $.merchandising["We're collecting data and building your dashboard."])}
          <br />
          {t(($) => $.merchandising["In the meantime explore the Trellis platform:"])}
        </p>
      )}
      {channel === "walmart" ?
        <Center>
          <CTA
            title={t(($) => $.common.sidebar["Advertising"])}
            to={"/user/advertising/dashboard"}
            toText={t(($) => $.merchandising["Create a media plan"])}
            icon={advertisingLogo}
          >
            <p>{t(($) => $.merchandising["Trellis’ bidding algorithm dramatically reduces wasted ad spend by:"])}</p>
            <ul>
              <li>{t(($) => $.merchandising["Harvesting and implementing keywords across the shoppers funnel"])}</li>
              <li>{t(($) => $.merchandising["Adjusting bids daily, and"])}</li>
              <li>{t(($) => $.merchandising["Reallocating budgets."])}</li>
            </ul>
          </CTA>
        </Center>
      : <Flex gap="large" direction={{ base: "column", lg: "row" }} justifyContent={"center"}>
          <CTA
            title={t(($) => $.common.sidebar["Advertising"])}
            to={"/user/advertising/dashboard"}
            toText={t(($) => $.merchandising["Create a media plan"])}
            icon={advertisingLogo}
          >
            <p>{t(($) => $.merchandising["Trellis’ bidding algorithm dramatically reduces wasted ad spend by:"])}</p>
            <ul>
              <li>{t(($) => $.merchandising["Harvesting and implementing keywords across the shoppers funnel"])}</li>
              <li>{t(($) => $.merchandising["Adjusting bids daily, and"])}</li>
              <li>{t(($) => $.merchandising["Reallocating budgets."])}</li>
            </ul>
          </CTA>
          <CTA
            title={t(($) => $.merchandising["Dynamic Pricing"])}
            to={"/user/pricing/dashboard"}
            toText={t(($) => $.merchandising["Enroll Products"])}
            icon={pricingLogo}
          >
            <p>
              {t(
                ($) =>
                  $.merchandising["By tracking and understanding your users' behavior, our tool can optimize for:"],
              )}
            </p>
            <ul>
              <li>{t(($) => $.merchandising["Sales growth"])}</li>
              <li>{t(($) => $.merchandising["Profit margins, or"])}</li>
              <li>{t(($) => $.merchandising["Net profit."])}</li>
            </ul>
          </CTA>
          <CTA
            title={t(($) => $.common.sidebar["Content"])}
            to={"/user/content/quality/org"}
            toText={t(($) => $.merchandising["View Content"])}
            icon={contentLogo}
          >
            <p>
              {t(
                ($) =>
                  $.merchandising[
                    "We provide content suggestions by analyzing your titles, A+ Content, images, price, and more to improve your:"
                  ],
              )}
            </p>
            <ul>
              <li>{t(($) => $.merchandising["Search Engine Optimization (SEO)"])}</li>
              <li>{t(($) => $.merchandising["The appeal of your listing, and"])}</li>
              <li>{t(($) => $.merchandising["Your listing's effectiveness at converting users."])}</li>
            </ul>
          </CTA>
        </Flex>
      }
    </PageContainer>
  );
};

export default EmptyState;
